/**
 * CUSTOM TABLE BODY ROWS VIEW TEMPLATE
 *
 * @version 0.1
 */

import React from 'react';
import { FormattedMessage } from 'react-intl';

import utils from './../../../utils/utilities';

import {
    Cell,
    EmptyCell,
    BooleanCell,
    LinkCell,
    EmailCell,
    ImageCell
} from './CustomTableBody.predefinedCellTypes.jsx';

/**
 * The row that is displayed when the table has no entries.
 *
 * @constructor
 * @return  {HTML}   The React NoEntriesRow component.
 */
export function NoEntriesRow({colspan, noEntries}) {

    let classes = 'custom-table-body__row--no-entries__cell';

    classes += (noEntries ? ' visible' : ' hidden');

    return (
        <tbody className="custom-table-body">
            <tr className="custom-table-body__row--no-entries">
                <td colSpan={colspan}
                    className={classes}>
                    <FormattedMessage id="General_CoreFunctionality_UIText_general.nothingToDisplay" />
                </td>
            </tr>
        </tbody>
    );

}

/**
 * This component is used to render the table rows, provided it has a pattern for building the rows and the table data.
 *
 * @constructor
 * @param   {object} props.table              The table body instance.
 * @param   {number} props.appendStartIndex   The row index at which new entries will be appended.
 * @param   {array}  props.rowsModel          The pattern that will be used to build the table rows.
 * @param   {array}  props.columnWidths       The array of row column widths, in percentages.
 * @return  {HTML}                            The Rows React component.
 */

export class Rows extends React.Component {

    constructor(props) {
        super(props);

        this.rows = [];
        this.emptyCellMessageId = this.props.table.state.emptyCellMessageId;
        this.tableId = 'table-' + utils.generateUUID();
    }

    render() {

        let tableData        = this.props.table.state.entries,
            appendStartIndex = this.props.appendStartIndex,
            rowsModel        = this.props.rowsModel,
            columnWidths     = this.props.columnWidths;

        // Clear the rows array if the append start index is 0 or doesn't exist
        if (!appendStartIndex) { this.rows = []; }

        // This line prevents row duplication in case of running multiple times the render method of the table body.
        // TODO: In the future we shoould think of another solution to prevent re-rendering as this is a quick but non-intuitive fix.
        appendStartIndex = this.rows.length > appendStartIndex ? this.rows.length : appendStartIndex;


        for (let i = appendStartIndex; i < tableData.length; i++) {

            let rowData = tableData[i],
                cells   = [];

            for (let j = 0; j < rowsModel.length; j++) {

                let cellValue = rowData[rowsModel[j].fieldId],
                    cellKey   = this.tableId + '-' + i + '-' + j;

                // The cell has no value
                if (typeof cellValue === 'undefined' || cellValue === null) {

                    cells.push(
                        <EmptyCell key={cellKey}
                                   columnWidth={columnWidths[j]}
                                   emptyCellMessageId={this.emptyCellMessageId} />
                    );

                    // The cell value was found
                } else {

                    let cellType    = rowsModel[j].fieldType,
                        cellFormat  = typeof rowsModel[j].format === 'function' ?
                            rowsModel[j].format.bind(this.props.table, cellValue, tableData, i, j) :
                            rowsModel[j].format,
                        cellProps   = {
                            key:         cellKey,
                            table:       this.props.table,
                            value:       cellValue,
                            format:      cellFormat,
                            classes:     rowsModel[j].classes,
                            columnWidth: columnWidths[j],
                            row:         i,
                            column:      j,
                            rowData:     rowData
                        },
                        customProps = rowsModel[j].props;

                    // The cell value is of primitive type and corresponds to the value type specified in the rows model
                    if (typeof cellValue === cellType || cellType === 'dynamic') {

                        switch (typeof cellValue) {

                            case 'boolean':
                                cells.push(<BooleanCell {...cellProps} booleanValues={rowsModel[j].booleanValues} />);
                                break;

                            case 'number':
                            case 'string':
                            default:
                                cells.push(<Cell {...cellProps} {...customProps} />);
                                break;

                        }

                        // In case the cell value is not of primitive type,
                        // then a React component is expected as the cell type
                    } else {

                        let CellComponent = cellType;

                        if (React.isValidElement(<CellComponent />)) {

                            // Create the table cell according to the React component type (predefined or custom cell)
                            switch (CellComponent) {

                                // Check if the cell is of a predefined cell type
                                case Cell:
                                    cells.push(<CellComponent {...cellProps} {...customProps} />);
                                    break;

                                case LinkCell:
                                    cells.push(
                                        <CellComponent key={cellProps.key}
                                                       value={cellProps.value}
                                                       classes={cellProps.classes}
                                                       columnWidth={cellProps.columnWidth}
                                                       {...customProps} />
                                    );
                                    break;

                                case EmailCell:
                                    cells.push(
                                        <CellComponent key={cellProps.key}
                                                       value={cellProps.value}
                                                       classes={cellProps.classes}
                                                       columnWidth={cellProps.columnWidth} />
                                    );
                                    break;

                                case ImageCell:
                                    cells.push(
                                        <CellComponent key={cellProps.key}
                                                       value={cellProps.value}
                                                       classes={cellProps.value}
                                                       columnWidth={cellProps.columnWidth}
                                                       altText={rowsModel[j].altText.bind(
                                                                null,
                                                                cellProps.value,
                                                                tableData,
                                                                i,
                                                                j)} />
                                    );
                                    break;

                                // Custom cell component
                                default:
                                    cells.push(
                                        <CellComponent {...cellProps} {...customProps} />
                                    );

                            }

                            // If the cell value is not of primitive type and also the cell
                            // type is not a React component, then render an empty cell
                        } else {
                            cells.push(
                                <EmptyCell key={cellKey}
                                           columnWidth={cellProps.columnWidth}
                                           emptyCellMessageId={this.emptyCellMessageId} />
                            );
                        }

                    }

                }

            }

            // Add the row to the rows array
            this.rows.push(<tr key={ this.tableId + '-' + i } className="custom-table-body__row">{cells}</tr>);

        }

        // Return the table body with all rows
        return (<tbody className="custom-table-body">{this.rows}</tbody>);

    }

}


// WEBPACK FOOTER //
// ./src/scripts/reusable/CustomTableBody/templates/CustomTableBody.rows.jsx